<template>
  <div>
    <div
      class="catalog-sidebar z-index__32"
      :class="{ active: isOpened }"
    >
      <div class="catalog-sidebar__header show-until-desktop">
        {{ $t('catalog.filters.categories_title') }}

        <div class="modal-body__close icon-times-light" @click="handleCancel" />
      </div>

      <CatalogCategoryFilterPreload
        v-if="fetchingContent"
      />

      <template v-else>
        <div class="categories">
          <div v-if="fetching" class="categories__loader z-index__1">
            <BaseLoader />
          </div>

          <div
            v-intersect
            class="category-filter"
            @intersect:enter="onShowCategryFilter"
          >
            <h3
              class="category-filter__title show-from-desktop"
              @click="toggleDropdown"
            >
              {{ title || $t('catalog.filters.categories_title') }}
              <span
                class="category-filter__title-arrow"
                :class="[`icon-chevron-${isShowCategories ? 'up' : 'down'}`]"
              />
            </h3>

            <transition
              name="accordion"
              @before-enter="transitionBeforeEnter"
              @enter="transitionEnter"
              @after-enter="transitionAfterEnter"
              @before-leave="transitionBeforeLeave"
              @leave="transitionLeave"
            >
              <div v-show="isShowCategories">
                <CatalogCategoryFilterItem
                  :category="allCategory"
                  :selected="selectedCategories"
                  :selected-nested-categories="selectedNestedCategories"
                  @on-change-category="handleChangeCategory"
                />

                <CatalogCategoryFilterItem
                  v-for="category in list"
                  :key="category.value"
                  :category="category"
                  :selected="selectedCategories"
                  :selected-nested-categories="selectedNestedCategories"
                  @on-change-category="handleChangeCategory"
                />
              </div>
            </transition>
          </div>
        </div>
      </template>

      <div class="catalog-sidebar__buttons show-until-desktop z-index__1">
        <BaseButton
          size="small"
          :loading="fetching"
          full-width
          outline
          @click="handleSubmit"
        >
          {{ $t('catalog.filters.show_products') }}
        </BaseButton>
      </div>
    </div>

    <div
      v-show="isOpened"
      ref="modal-mask-ref"
      class="catalog-sidebar-mask z-index__25 show-until-desktop"
      @click="handleCancel"
    />
  </div>
</template>

<script setup lang="ts">
import type { FilterCategory } from '~/types/catalog'

type Props = {
  isOpened: boolean
  title: string
}

type Emits = {
  (e: 'close-modal' | 'change-category'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()
const i18n = useI18n()
const commonMetricsStore = useCommonMetricsStore()
const catalogCategoriesStore = useCatalogCategoriesStore()
const catalogFiltersStore = useCatalogFiltersStore()

const {
  fetching,
  fetchingContent,
  list,
  selectedCategories,
  selectedNestedCategories
} = storeToRefs(catalogCategoriesStore)

const isShowCategories = shallowRef(true)
const modalMask = useTemplateRef<HTMLDivElement>('modal-mask-ref')

const {
  stopScrollSubscribe,
  stopScrollUnsubscribe
} = useOverflowed()

const {
  transitionBeforeEnter,
  transitionEnter,
  transitionAfterEnter,
  transitionBeforeLeave,
  transitionLeave
} = useTransition()

const allCategory = computed<FilterCategory>(() => {
  return {
    value: '',
    name: i18n.t('catalog.filters.categories_all') as string,
    uuid: '',
    image: '',
    slug: '',
    count: 0,
    parent: null,
    children: []
  }
})

const toggleDropdown = () => {
  isShowCategories.value = !isShowCategories.value
}

const handleSubmit = () => {
  handleCancel()
  commonMetricsStore.addMetricEvent({
    element: 'ms[catalog-categories-filter-submit]',
    event_type: 'click',
    event_content: ''
  })
}

const handleChangeCategory = (category: FilterCategory) => {
  // if (props.isOpened) {
  catalogFiltersStore.changeCategory(category.value)
  catalogCategoriesStore.changeSelectedCategory(category.value)
    // return
  // }

  emit('change-category')
  commonMetricsStore.addMetricEvent({
    element: 'ms[catalog-categories-filter]',
    event_type: 'click',
    event_content: { name: category.name, code: category.value }
  })
}

const onShowCategryFilter = () => {
  commonMetricsStore.addMetricEvent({
    element: 'ms[catalog-categories-filter]',
    event_type: 'view',
    event_content: ''
  })
}

const handleCancel = () => {
  emit('close-modal')
}

watch(() => props.isOpened, (show: boolean) => {
  if (show) {
    stopScrollSubscribe(modalMask.value!)
  } else {
    stopScrollUnsubscribe(modalMask.value!)
  }
})

onBeforeUnmount(() => {
  stopScrollUnsubscribe(modalMask.value!)
})
</script>

<style lang="scss" scoped>
.categories {
  position: relative;
  padding-bottom: 0.8rem;
  margin-bottom: 1.6rem;
  border-bottom: solid 1px #e5e5e5;

  @media only screen and (max-width: 1024px) {
    overflow: auto;
    height: calc(100% - #{5.8+7.6+rem});
    margin: 0;
    padding: 0 1.6rem;
  }
  @media only screen and (max-width: 740px) {
    height: calc(100% - #{5.8+7.2+rem});
  }

  &__loader {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba($white-color, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.category-filter {
  &__title {
    font-size: 1.4rem;
    line-height: 2.1rem;
    font-weight: 500;
    display: flex;
    justify-content: space-between;
    margin-bottom: 1rem;
    cursor: pointer;

    &-arrow {
      padding-left: 1rem;
      vertical-align: baseline;
      color: #8a8a8a;
      position: relative;
      top: 0.2rem;
    }
  }
}

.accordion {
  &-leave-active,
  &-enter-active {
    overflow-y: hidden;
    transition: 0.25s ease;
    transition-property: max-height, padding;
  }

  &-leave-active {
    padding-top: 0;
    padding-bottom: 0;
  }
}

.modal-body {
  &__close {
    position: absolute;
    top: 1rem;
    right: 1.2rem;

    @media only screen and (min-width: 740px) {
      top: 1.4rem;
      font-size: 2.4rem;
    }
  }
}
</style>
